import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Card,
  Descriptions,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Tag,
  Alert,
  Statistic,
  Tabs,
  Form,
  Input,
  Radio,
  Checkbox,
  message,
  Modal,
  Breadcrumb,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  StarOutlined,
  DollarOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface DetailedAssessmentReviewData {
  projectCode: string;
  projectName: string;
  issuerName: string;
  submissionDate: string;
  completionRate: number;

  // 智能风险分析结果
  riskAnalysis: {
    overallRiskLevel: 'low' | 'medium' | 'high';
    financialRisks: string[];
    businessRisks: string[];
    complianceRisks: string[];
    recommendations: string[];
  };

  // 关键指标摘要
  keyMetrics: {
    totalAssets: number;
    debtRatio: number;
    roe: number;
    creditRating: string;
    greenProjectInvestment: number;
    expectedIssuanceSize: string;
  };

  // 各模块详细数据
  assessmentData: any;
}

const DetailedAssessmentReview: React.FC = () => {
  const { projectCode } = useParams<{ projectCode: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [reviewData, setReviewData] =
    useState<DetailedAssessmentReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<
    'approved' | 'rejected' | 'supplement'
  >('approved');

  // 模拟数据加载
  useEffect(() => {
    const loadReviewData = async () => {
      try {
        // TODO: 调用API获取详细评估数据
        const mockData: DetailedAssessmentReviewData = {
          projectCode: projectCode!,
          projectName: t('assessment:review.mock.projectName'),
          issuerName: t('assessment:review.mock.issuerName'),
          submissionDate: '2025-09-25 16:30:00',
          completionRate: 100,
          riskAnalysis: {
            overallRiskLevel: 'medium',
            financialRisks: [
              t('assessment:review.mock.risk.financial.one'),
              t('assessment:review.mock.risk.financial.two'),
            ],
            businessRisks: [
              t('assessment:review.mock.risk.business.one'),
              t('assessment:review.mock.risk.business.two'),
            ],
            complianceRisks: [t('assessment:review.mock.risk.compliance.one')],
            recommendations: [
              t('assessment:review.mock.recommendations.one'),
              t('assessment:review.mock.recommendations.two'),
              t('assessment:review.mock.recommendations.three'),
              t('assessment:review.mock.recommendations.four'),
            ],
          },
          keyMetrics: {
            totalAssets: 2000000,
            debtRatio: 65,
            roe: 12.5,
            creditRating: 'AA',
            greenProjectInvestment: 150000,
            expectedIssuanceSize: t(
              'assessment:review.mock.expectedIssuanceSize'
            ),
          },
          assessmentData: {
            // 详细的评估数据...
          },
        };

        setReviewData(mockData);
      } catch (error) {
        console.error('Failed to load detailed assessment data:', error);
        message.error(t('assessment:review.message.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (projectCode) {
      loadReviewData();
    }
  }, [projectCode, t]);

  // 提交审核决策
  const handleSubmitReview = async () => {
    try {
      const values = await form.validateFields();

      // TODO: 调用API提交审核决策
      console.log('Review decision:', {
        projectCode,
        decision: reviewDecision,
        comments: values.comments,
        reasons: values.reasons,
      });

      message.success(t('assessment:review.message.submitSuccess'));
      setReviewModalVisible(false);

      // 跳转回筛选工作台
      navigate('/admin/screening');
    } catch (error) {
      console.error('Failed to submit review decision:', error);
      message.error(t('assessment:review.message.submitFailed'));
    }
  };

  // 渲染风险分析
  const renderRiskAnalysis = () => {
    if (!reviewData) return null;

    const { riskAnalysis } = reviewData;
    const riskLevelConfig = {
      low: {
        color: '#52c41a',
        text: t('assessment:review.risk.level.low'),
        icon: <CheckCircleOutlined />,
      },
      medium: {
        color: '#faad14',
        text: t('assessment:review.risk.level.medium'),
        icon: <ExclamationCircleOutlined />,
      },
      high: {
        color: '#ff4d4f',
        text: t('assessment:review.risk.level.high'),
        icon: <WarningOutlined />,
      },
    };

    const config = riskLevelConfig[riskAnalysis.overallRiskLevel];

    return (
      <Card
        title={t('assessment:review.risk.title')}
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {config.icon}
                  <span style={{ marginLeft: '8px' }}>
                    {t('assessment:review.risk.overallLabel')}{' '}
                    <strong style={{ color: config.color }}>
                      {config.text}
                    </strong>
                  </span>
                </div>
              }
              type={
                riskAnalysis.overallRiskLevel === 'low'
                  ? 'success'
                  : riskAnalysis.overallRiskLevel === 'medium'
                    ? 'warning'
                    : 'error'
              }
              showIcon={false}
              style={{ marginBottom: '16px' }}
            />
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col span={8}>
            <Card size="small" title={t('assessment:review.risk.financial')}>
              {riskAnalysis.financialRisks.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {riskAnalysis.financialRisks.map((risk, index) => (
                    <li
                      key={index}
                      style={{ marginBottom: '4px', color: '#ff4d4f' }}
                    >
                      {risk}
                    </li>
                  ))}
                </ul>
              ) : (
                <Text type="secondary">
                  {t('assessment:review.risk.noneFinancial')}
                </Text>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title={t('assessment:review.risk.business')}>
              {riskAnalysis.businessRisks.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {riskAnalysis.businessRisks.map((risk, index) => (
                    <li
                      key={index}
                      style={{ marginBottom: '4px', color: '#faad14' }}
                    >
                      {risk}
                    </li>
                  ))}
                </ul>
              ) : (
                <Text type="secondary">
                  {t('assessment:review.risk.noneBusiness')}
                </Text>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title={t('assessment:review.risk.compliance')}>
              {riskAnalysis.complianceRisks.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {riskAnalysis.complianceRisks.map((risk, index) => (
                    <li
                      key={index}
                      style={{ marginBottom: '4px', color: '#faad14' }}
                    >
                      {risk}
                    </li>
                  ))}
                </ul>
              ) : (
                <Text type="secondary">
                  {t('assessment:review.risk.noneCompliance')}
                </Text>
              )}
            </Card>
          </Col>
        </Row>

        {riskAnalysis.recommendations.length > 0 && (
          <Card
            size="small"
            title={t('assessment:review.risk.recommendations')}
            style={{ marginTop: '16px' }}
          >
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {riskAnalysis.recommendations.map((recommendation, index) => (
                <li
                  key={index}
                  style={{ marginBottom: '4px', color: '#1890ff' }}
                >
                  {recommendation}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </Card>
    );
  };

  // 渲染关键指标
  const renderKeyMetrics = () => {
    if (!reviewData) return null;

    const { keyMetrics } = reviewData;

    return (
      <Card
        title={t('assessment:review.metrics.title')}
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[24, 16]}>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.totalAssets')}
              value={keyMetrics.totalAssets}
              suffix={t('assessment:review.metrics.tenThousandYuan')}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.debtRatio')}
              value={keyMetrics.debtRatio}
              suffix="%"
              valueStyle={{
                color:
                  keyMetrics.debtRatio > 70
                    ? '#ff4d4f'
                    : keyMetrics.debtRatio > 60
                      ? '#faad14'
                      : '#52c41a',
              }}
              prefix={<WarningOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.roe')}
              value={keyMetrics.roe}
              suffix="%"
              valueStyle={{ color: '#52c41a' }}
              prefix={<StarOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.creditRating')}
              value={keyMetrics.creditRating}
              prefix={<SafetyOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.greenInvestment')}
              value={keyMetrics.greenProjectInvestment}
              suffix={t('assessment:review.metrics.tenThousandYuan')}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              prefix={<EnvironmentOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('assessment:review.metrics.expectedIssuance')}
              value={keyMetrics.expectedIssuanceSize}
              prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  // 渲染审核决策模态框
  const renderReviewModal = () => (
    <Modal
      title={t('assessment:review.modal.title')}
      visible={reviewModalVisible}
      onCancel={() => setReviewModalVisible(false)}
      width={600}
      footer={[
        <Button key="cancel" onClick={() => setReviewModalVisible(false)}>
          {t('common:action.cancel')}
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmitReview}>
          {t('assessment:review.modal.submit')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('assessment:review.modal.decision')}
          name="decision"
          rules={[
            {
              required: true,
              message: t('assessment:review.modal.validation.selectDecision'),
            },
          ]}
          initialValue="approved"
        >
          <Radio.Group onChange={e => setReviewDecision(e.target.value)}>
            <Space direction="vertical">
              <Radio value="approved">
                <CheckCircleOutlined style={{ color: '#52c41a' }} />{' '}
                {t('assessment:review.modal.options.approved')}
              </Radio>
              <Radio value="rejected">
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} />{' '}
                {t('assessment:review.modal.options.rejected')}
              </Radio>
              <Radio value="supplement">
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />{' '}
                {t('assessment:review.modal.options.supplement')}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item label={t('assessment:review.modal.reasons')} name="reasons">
          <Checkbox.Group>
            <Row gutter={[8, 8]}>
              {reviewDecision === 'approved' && [
                <Col span={24} key="1">
                  <Checkbox value="approved_financial_health">
                    {t(
                      'assessment:review.modal.reasonOptions.approved.financialHealth'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="2">
                  <Checkbox value="approved_green_standard">
                    {t(
                      'assessment:review.modal.reasonOptions.approved.greenStandard'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="3">
                  <Checkbox value="approved_issuance_reasonable">
                    {t(
                      'assessment:review.modal.reasonOptions.approved.issuanceReasonable'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="4">
                  <Checkbox value="approved_risk_controllable">
                    {t(
                      'assessment:review.modal.reasonOptions.approved.riskControllable'
                    )}
                  </Checkbox>
                </Col>,
              ]}
              {reviewDecision === 'rejected' && [
                <Col span={24} key="1">
                  <Checkbox value="rejected_financial_risk">
                    {t(
                      'assessment:review.modal.reasonOptions.rejected.financialRisk'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="2">
                  <Checkbox value="rejected_green_noncompliance">
                    {t(
                      'assessment:review.modal.reasonOptions.rejected.greenNoncompliance'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="3">
                  <Checkbox value="rejected_compliance_issue">
                    {t(
                      'assessment:review.modal.reasonOptions.rejected.complianceIssue'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="4">
                  <Checkbox value="rejected_issuance_condition">
                    {t(
                      'assessment:review.modal.reasonOptions.rejected.issuanceCondition'
                    )}
                  </Checkbox>
                </Col>,
              ]}
              {reviewDecision === 'supplement' && [
                <Col span={24} key="1">
                  <Checkbox value="supplement_financial_docs">
                    {t(
                      'assessment:review.modal.reasonOptions.supplement.financialDocs'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="2">
                  <Checkbox value="supplement_green_certification">
                    {t(
                      'assessment:review.modal.reasonOptions.supplement.greenCertification'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="3">
                  <Checkbox value="supplement_legal_docs">
                    {t(
                      'assessment:review.modal.reasonOptions.supplement.legalDocs'
                    )}
                  </Checkbox>
                </Col>,
                <Col span={24} key="4">
                  <Checkbox value="supplement_related_party">
                    {t(
                      'assessment:review.modal.reasonOptions.supplement.relatedParty'
                    )}
                  </Checkbox>
                </Col>,
              ]}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          label={t('assessment:review.modal.comments')}
          name="comments"
          rules={[
            {
              required: true,
              message: t('assessment:review.modal.validation.enterComments'),
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder={t('assessment:review.modal.commentsPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content
          style={{ padding: '24px', textAlign: 'center', paddingTop: '200px' }}
        >
          <div>{t('common:status.loading')}</div>
        </Content>
      </Layout>
    );
  }

  if (!reviewData) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content
          style={{ padding: '24px', textAlign: 'center', paddingTop: '200px' }}
        >
          <div>{t('assessment:review.noData')}</div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        {/* 页面头部 */}
        <div style={{ marginBottom: '24px' }}>
          <Breadcrumb style={{ marginBottom: '16px' }}>
            <Breadcrumb.Item>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/admin/screening')}
                style={{ padding: 0 }}
              >
                {t('assessment:review.breadcrumb.screeningWorkbench')}
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {t('assessment:review.breadcrumb.review')}
            </Breadcrumb.Item>
          </Breadcrumb>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
                {reviewData.projectName}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {reviewData.projectCode} • {reviewData.issuerName} •{' '}
                {t('assessment:review.submissionTime')}:{' '}
                {reviewData.submissionDate}
              </Text>
            </div>
            <Space>
              <Tag color="green">
                {t('assessment:review.completion')}: {reviewData.completionRate}
                %
              </Tag>
              <Button
                type="primary"
                size="large"
                onClick={() => setReviewModalVisible(true)}
              >
                {t('assessment:review.reviewAction')}
              </Button>
            </Space>
          </div>
        </div>

        {/* 智能风险分析 */}
        {renderRiskAnalysis()}

        {/* 关键指标概览 */}
        {renderKeyMetrics()}

        {/* 详细信息展示 */}
        <Tabs defaultActiveKey="overview" size="large">
          <TabPane
            tab={
              <span>
                <TeamOutlined />
                {t('assessment:review.tabs.issuer')}
              </span>
            }
            key="issuer"
          >
            <Card>
              <Descriptions
                title={t('assessment:review.sections.companyInfo')}
                bordered
                column={2}
              >
                <Descriptions.Item
                  label={t('assessment:review.labels.companyNameCn')}
                >
                  {t('assessment:review.mock.issuerName')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.companyNameEn')}
                >
                  End-to-End Test Company_e2e
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.registeredAddress')}
                >
                  {t('assessment:review.mock.registeredAddress')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.creditCode')}
                >
                  91110000000000000X
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.registeredCapital')}
                >
                  {t('assessment:review.mock.registeredCapital')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.paidCapital')}
                >
                  {t('assessment:review.mock.paidCapital')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.establishmentDate')}
                >
                  2020-01-01
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.controllingShareholder')}
                >
                  {t('assessment:review.mock.controllingShareholder')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.controllingRatio')}
                >
                  51%
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.actualController')}
                >
                  {t('assessment:review.mock.actualController')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.equityPledge')}
                >
                  {t('assessment:review.mock.none')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined />
                {t('assessment:review.tabs.financial')}
              </span>
            }
            key="financial"
          >
            <Card>
              <Descriptions
                title={t('assessment:review.sections.mainFinancialMetrics')}
                bordered
                column={3}
              >
                <Descriptions.Item
                  label={t('assessment:review.labels.totalAssets')}
                >
                  {t('assessment:review.mock.totalAssetsDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.totalLiabilities')}
                >
                  {t('assessment:review.mock.totalLiabilitiesDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.netAssets')}
                >
                  {t('assessment:review.mock.netAssetsDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.revenue')}
                >
                  {t('assessment:review.mock.revenueDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.netProfit')}
                >
                  {t('assessment:review.mock.netProfitDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.operatingCashflow')}
                >
                  {t('assessment:review.mock.operatingCashflowDisplay')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.debtRatio')}
                >
                  65%
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.currentRatio')}
                >
                  1.2
                </Descriptions.Item>
                <Descriptions.Item label={t('assessment:review.labels.roe')}>
                  12.5%
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <EnvironmentOutlined />
                {t('assessment:review.tabs.green')}
              </span>
            }
            key="green"
          >
            <Card>
              <Descriptions
                title={t('assessment:review.sections.greenProjectInfo')}
                bordered
                column={2}
              >
                <Descriptions.Item
                  label={t('assessment:review.labels.projectName')}
                >
                  {t('assessment:review.mock.greenProjectName')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.projectAddress')}
                >
                  {t('assessment:review.mock.greenProjectAddress')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.projectNature')}
                >
                  {t('assessment:review.mock.projectNature')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.projectTotalInvestment')}
                >
                  {t('assessment:review.mock.projectTotalInvestment')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.constructionPeriod')}
                >
                  {t('assessment:review.mock.constructionPeriod')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.operationPeriod')}
                >
                  {t('assessment:review.mock.operationPeriod')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.annualEnergySaving')}
                >
                  {t('assessment:review.mock.annualEnergySaving')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.annualCO2Reduction')}
                >
                  {t('assessment:review.mock.annualCO2Reduction')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.greenStandards')}
                >
                  {t('assessment:review.mock.greenStandards')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.thirdPartyCertification')}
                >
                  {t('assessment:review.mock.thirdPartyCertification')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                {t('assessment:review.tabs.issuance')}
              </span>
            }
            key="issuance"
          >
            <Card>
              <Descriptions
                title={t('assessment:review.sections.issuancePlan')}
                bordered
                column={2}
              >
                <Descriptions.Item
                  label={t('assessment:review.labels.expectedIssuanceSize')}
                >
                  {t('assessment:review.mock.expectedIssuanceSize')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.expectedTerm')}
                >
                  {t('assessment:review.mock.expectedTerm')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.guaranteeStructure')}
                >
                  {t('assessment:review.mock.guaranteeStructure')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.targetExchange')}
                >
                  {t('assessment:review.mock.targetExchange')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.expectedCouponRate')}
                >
                  3.8%
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.underwriterPreference')}
                >
                  {t('assessment:review.mock.underwriterPreference')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.legalCounsel')}
                >
                  {t('assessment:review.mock.legalCounsel')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.ratingAgency')}
                >
                  {t('assessment:review.mock.ratingAgency')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <WarningOutlined />
                {t('assessment:review.tabs.risk')}
              </span>
            }
            key="risk"
          >
            <Card>
              <Descriptions
                title={t('assessment:review.sections.riskManagement')}
                bordered
                column={2}
              >
                <Descriptions.Item
                  label={t('assessment:review.labels.regulatoryStatus')}
                >
                  {t('assessment:review.mock.regulatoryStatus')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.environmentalCompliance')}
                >
                  {t('assessment:review.mock.environmentalCompliance')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.legalDisputes')}
                >
                  {t('assessment:review.mock.legalDisputes')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.relatedPartyTransactions')}
                >
                  {t('assessment:review.mock.relatedPartyTransactions')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.externalGuarantees')}
                >
                  {t('assessment:review.mock.externalGuarantees')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('assessment:review.labels.assetMortgage')}
                >
                  {t('assessment:review.mock.assetMortgage')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>
        </Tabs>

        {/* 审核决策模态框 */}
        {renderReviewModal()}
      </Content>
    </Layout>
  );
};

export default DetailedAssessmentReview;
